import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Star, MessageSquare, CheckCircle, XCircle, Clock, Send, Upload, RefreshCw,
  Loader2, MoreVertical, Trash2, Reply, ThumbsUp, ThumbsDown, Filter, Building2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Star Rating Component
const StarRating = ({ rating, size = 'md', showNumber = true }) => {
  const sizes = { sm: 'w-3 h-3', md: 'w-4 h-4', lg: 'w-5 h-5' };
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizes[size]} ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
        />
      ))}
      {showNumber && <span className="ml-1 text-sm font-medium text-gray-600">{rating.toFixed(1)}</span>}
    </div>
  );
};

// Interactive Star Rating for forms
const StarRatingInput = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          className="p-1 transition-transform hover:scale-110"
        >
          <Star
            className={`w-8 h-8 transition-colors ${
              star <= (hover || value) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

export default function ReviewsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [filterProperty, setFilterProperty] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Dialog states
  const [respondDialogOpen, setRespondDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Import form
  const [importForm, setImportForm] = useState({
    property_id: '',
    source: 'airbnb',
    rating: 5,
    title: '',
    content: '',
    guest_name: '',
    review_date: new Date().toISOString().split('T')[0]
  });

  const getToken = () => localStorage.getItem('ihostpms_token');

  const loadReviews = useCallback(async () => {
    try {
      let url = `${API_URL}/api/reviews`;
      const params = new URLSearchParams();
      if (filterProperty !== 'all') params.append('property_id', filterProperty);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (params.toString()) url += `?${params.toString()}`;

      const resp = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (resp.ok) {
        setReviews(await resp.json());
      }
    } catch (err) {
      console.error('Load reviews error:', err);
    }
  }, [filterProperty, filterStatus]);

  const loadStats = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/reviews/stats`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (resp.ok) {
        setStats(await resp.json());
      }
    } catch (err) {
      console.error('Load stats error:', err);
    }
  }, []);

  const loadProperties = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/properties`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (resp.ok) {
        setProperties(await resp.json());
      }
    } catch (err) {
      console.error('Load properties error:', err);
    }
  }, []);

  useEffect(() => {
    Promise.all([loadReviews(), loadStats(), loadProperties()]).finally(() => setLoading(false));
  }, [loadReviews, loadStats, loadProperties]);

  useEffect(() => {
    loadReviews();
  }, [filterProperty, filterStatus, loadReviews]);

  const handleApprove = async (reviewId) => {
    setProcessing(true);
    try {
      const resp = await fetch(`${API_URL}/api/reviews/${reviewId}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (resp.ok) {
        toast({ title: 'Review Approved', description: 'The review is now visible to guests.' });
        loadReviews();
        loadStats();
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to approve review', variant: 'destructive' });
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (reviewId) => {
    setProcessing(true);
    try {
      const resp = await fetch(`${API_URL}/api/reviews/${reviewId}/reject`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (resp.ok) {
        toast({ title: 'Review Rejected', description: 'The review has been rejected.' });
        loadReviews();
        loadStats();
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to reject review', variant: 'destructive' });
    } finally {
      setProcessing(false);
    }
  };

  const handleRespond = async () => {
    if (!selectedReview || !responseText.trim()) return;
    setProcessing(true);
    try {
      const resp = await fetch(`${API_URL}/api/reviews/${selectedReview.review_id}/respond`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ response: responseText })
      });
      if (resp.ok) {
        toast({ title: 'Response Added', description: 'Your response has been saved.' });
        setRespondDialogOpen(false);
        setResponseText('');
        loadReviews();
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to add response', variant: 'destructive' });
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedReview) return;
    setProcessing(true);
    try {
      const resp = await fetch(`${API_URL}/api/reviews/${selectedReview.review_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (resp.ok) {
        toast({ title: 'Review Deleted' });
        setDeleteDialogOpen(false);
        loadReviews();
        loadStats();
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete review', variant: 'destructive' });
    } finally {
      setProcessing(false);
    }
  };

  const handleImport = async () => {
    if (!importForm.property_id || !importForm.content || !importForm.guest_name) {
      toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }
    setProcessing(true);
    try {
      const resp = await fetch(`${API_URL}/api/reviews/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(importForm)
      });
      if (resp.ok) {
        toast({ title: 'Review Imported', description: `Review from ${importForm.source} has been added.` });
        setImportDialogOpen(false);
        setImportForm({
          property_id: '',
          source: 'airbnb',
          rating: 5,
          title: '',
          content: '',
          guest_name: '',
          review_date: new Date().toISOString().split('T')[0]
        });
        loadReviews();
        loadStats();
      } else {
        const data = await resp.json();
        toast({ title: 'Error', description: data.detail || 'Failed to import review', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to import review', variant: 'destructive' });
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" /> Published</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getSourceBadge = (source) => {
    const sources = {
      direct: { label: 'Direct', class: 'bg-blue-100 text-blue-700' },
      airbnb: { label: 'Airbnb', class: 'bg-pink-100 text-pink-700' },
      booking_com: { label: 'Booking.com', class: 'bg-indigo-100 text-indigo-700' }
    };
    const s = sources[source] || { label: source, class: 'bg-gray-100 text-gray-700' };
    return <Badge className={s.class}>{s.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-airbnb-pink" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reviews-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Guest Reviews</h1>
          <p className="text-gray-500 mt-1">Manage and respond to guest feedback</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { loadReviews(); loadStats(); }} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setImportDialogOpen(true)} data-testid="import-review-btn">
            <Upload className="w-4 h-4 mr-2" />
            Import Review
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Star className="w-5 h-5 text-yellow-600 fill-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.average_rating}</p>
                <p className="text-sm text-gray-500">Avg Rating</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MessageSquare className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total_reviews}</p>
                <p className="text-sm text-gray-500">Total Reviews</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.pending_count}</p>
                <p className="text-sm text-gray-500">Pending</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <ThumbsUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.rating_distribution[5] || 0}</p>
                <p className="text-sm text-gray-500">5-Star Reviews</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filter:</span>
        </div>
        <Select value={filterProperty} onValueChange={setFilterProperty}>
          <SelectTrigger className="w-[180px]" data-testid="filter-property">
            <Building2 className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Properties" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Properties</SelectItem>
            {properties.map((p) => (
              <SelectItem key={p.property_id} value={p.property_id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[140px]" data-testid="filter-status">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Published</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {reviews.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="font-medium text-gray-600">No reviews yet</p>
            <p className="text-sm text-gray-500 mt-1">Reviews will appear here when guests submit feedback</p>
          </div>
        ) : (
          reviews.map((review) => (
            <div key={review.review_id} className="bg-white rounded-xl border p-5" data-testid={`review-${review.review_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <StarRating rating={review.rating} />
                    {getStatusBadge(review.status)}
                    {getSourceBadge(review.source)}
                  </div>
                  {review.title && (
                    <h3 className="font-semibold text-gray-900 mb-1">{review.title}</h3>
                  )}
                  <p className="text-gray-600 mb-3">{review.content}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="font-medium">{review.guest_name}</span>
                    <span>•</span>
                    <span>{review.property_name}</span>
                    <span>•</span>
                    <span>{new Date(review.created_at).toLocaleDateString()}</span>
                  </div>
                  
                  {/* Host Response */}
                  {review.host_response && (
                    <div className="mt-4 bg-gray-50 rounded-lg p-4 border-l-4 border-airbnb-pink">
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                        <Reply className="w-4 h-4" />
                        Host Response
                      </div>
                      <p className="text-gray-600 text-sm">{review.host_response}</p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" data-testid={`review-actions-${review.review_id}`}>
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {review.status === 'pending' && (
                      <>
                        <DropdownMenuItem onClick={() => handleApprove(review.review_id)}>
                          <CheckCircle className="w-4 h-4 mr-2 text-emerald-600" />
                          Approve
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleReject(review.review_id)}>
                          <XCircle className="w-4 h-4 mr-2 text-red-600" />
                          Reject
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuItem onClick={() => { setSelectedReview(review); setResponseText(review.host_response || ''); setRespondDialogOpen(true); }}>
                      <Reply className="w-4 h-4 mr-2" />
                      {review.host_response ? 'Edit Response' : 'Respond'}
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-red-600" onClick={() => { setSelectedReview(review); setDeleteDialogOpen(true); }}>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Respond Dialog */}
      <Dialog open={respondDialogOpen} onOpenChange={setRespondDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Respond to Review</DialogTitle>
            <DialogDescription>Your response will be visible to future guests.</DialogDescription>
          </DialogHeader>
          {selectedReview && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <StarRating rating={selectedReview.rating} size="sm" />
              <p className="text-sm text-gray-600 mt-2">{selectedReview.content}</p>
              <p className="text-xs text-gray-500 mt-2">— {selectedReview.guest_name}</p>
            </div>
          )}
          <Textarea
            value={responseText}
            onChange={(e) => setResponseText(e.target.value)}
            placeholder="Write your response..."
            rows={4}
            data-testid="response-textarea"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRespondDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleRespond} disabled={processing || !responseText.trim()} data-testid="submit-response-btn">
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {selectedReview?.host_response ? 'Update Response' : 'Post Response'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import Review from OTA</DialogTitle>
            <DialogDescription>Add a review from Airbnb or Booking.com</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Property *</Label>
              <Select value={importForm.property_id} onValueChange={(v) => setImportForm({ ...importForm, property_id: v })}>
                <SelectTrigger className="mt-1" data-testid="import-property">
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.property_id} value={p.property_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Source</Label>
                <Select value={importForm.source} onValueChange={(v) => setImportForm({ ...importForm, source: v })}>
                  <SelectTrigger className="mt-1" data-testid="import-source">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="airbnb">Airbnb</SelectItem>
                    <SelectItem value="booking_com">Booking.com</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Review Date</Label>
                <Input
                  type="date"
                  value={importForm.review_date}
                  onChange={(e) => setImportForm({ ...importForm, review_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Rating</Label>
              <div className="mt-2">
                <StarRatingInput value={importForm.rating} onChange={(v) => setImportForm({ ...importForm, rating: v })} />
              </div>
            </div>
            <div>
              <Label>Guest Name *</Label>
              <Input
                value={importForm.guest_name}
                onChange={(e) => setImportForm({ ...importForm, guest_name: e.target.value })}
                placeholder="Guest name"
                className="mt-1"
                data-testid="import-guest-name"
              />
            </div>
            <div>
              <Label>Review Title</Label>
              <Input
                value={importForm.title}
                onChange={(e) => setImportForm({ ...importForm, title: e.target.value })}
                placeholder="Review title (optional)"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Review Content *</Label>
              <Textarea
                value={importForm.content}
                onChange={(e) => setImportForm({ ...importForm, content: e.target.value })}
                placeholder="Review content..."
                rows={3}
                className="mt-1"
                data-testid="import-content"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleImport} disabled={processing} data-testid="confirm-import-btn">
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Import Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Review?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this review. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
